import React, {
  createContext,
  useReducer,
  useEffect,
  ReactNode,
  useState,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  blockRequest,
  UnblockRequest,
  DeliveredRequest,
  SeenRequest,
  TypingRequest,
  BlockResponse,
  BlockListResponse,
  ArchiveRoomRequest,
  UnBlockResponse,
  ArchiveRoomResponse,
  DeliveredResponse,
  SeenResponse,
  GetRoomsRequest,
  GetRoomsResponse,
  GetMessagesRequest,
  GetMessagesResponse,
  ResponseError,
  CheckOnlineStatusRequest,
  CheckOnlineStatusResponse,
  SendMessageResponse,
  DeleteMessageRequest,
  DeleteMessageResponse,
  Room,
  SendMessageRequest,
  MessageFromRoom,
  Message,
  UnreadRoomsResponse,
  UnreadPostRoomsResponse,
  handleTypingEventInterface,
  filterRequest,
  userInfoRequest,
  UpdateOnlineStatusPayload,
  SeenAllResponse,
  SeenAllRequest,
  UnreadPostRoomsRequest,
  UserInfoResponse,
  UserStatus,
} from '../utils/types';
import {emitEvent} from '../utils/EmitEvent';
import {ChatEvents, SocketActionTypes, SocketEvents} from '../utils/enums';
import {initialState, roomHasUser, socketReducer} from './socketReducer';
import {AuthContext} from '../../../i18n/AuthProvider';
import {Socket} from 'socket.io-client';
import {
  generateOpenSooqRedirectUrl,
  generateToken,
  getCookies,
} from '../../../helpers/commonFunctions';
import Cookies from 'js-cookie';
import getListingInfoV2List from '../../../../api/listing/get-listing-info-v2-list';
import {initialContext, SocketContextProps} from './InitialContext';
import {useLocation, useNavigate} from 'react-router-dom';
import {useLang} from '../../../i18n/OpenSooqi18n';
import { logMessageClick } from '../../../../logging/helpers/commonLoggingFunctions';
import getMemberInfoApi from '../../../../api/listing/get-member-info.api';
import {
  mergeParticipant,
  participantFromListingSeller,
  participantFromProfile,
} from '../utils/chatParticipants';
import {buildSocketAuth, getSocket, resetSocket} from './socketService';
import {generateRandomNumber} from '../utils/helpers';
const SocketContext = createContext<SocketContextProps>(initialContext);

export const SocketContexWtProvider: React.FC<{
  children: ReactNode;
}> = ({children}) => {
  const [state, dispatch] = useReducer(socketReducer, initialState);
  const [socket, setSocket] = useState<Socket | null>(null);
  const typingTimeoutRef = useRef<{[room_id: string]: NodeJS.Timeout}>({});
  const {userIdentity} = useContext(AuthContext);
  const selfParticipant = useMemo(
    () => mergeParticipant(participantFromProfile(userIdentity), null),
    [userIdentity]
  );
  const identityRef = useRef(userIdentity);
  identityRef.current = userIdentity;
  const sessionRef = useRef(0);
  const sendingRef = useRef(new Set<string>());
  const failedMessagesRef = useRef(state.failedMessages);
  failedMessagesRef.current = state.failedMessages;
  const filteredRoomsRequestRef = useRef(0);
  const messageRequestsRef = useRef(new Map<string, number>());
  const lang = useLang();
  const navigate = useNavigate();
  function updateUnreadRoomsCount(count: number) {
    const data = {
      data: {
        success: true,
        rooms_unread_count: count,
      },
    };

    dispatch({type: SocketActionTypes.ROOMS_UNREAD_COUNT, response: data});
  }
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    dispatch({type: SocketActionTypes.RESET_SESSION});
    messageRequestsRef.current.clear();
    return () => { sessionRef.current++; };
  }, [userIdentity?.id]);

  useEffect(() => {
    if (!userIdentity?.id) {
      return;
    }

    let isMounted = true;
    let activeSocket: Socket | null = null;

    const handleConnect = () => {
      dispatch({type: SocketActionTypes.CONNECT});
    };

    const handleConnectError = (err: Error) => {
      console.error("Connection Error:", err.message);
      dispatch({type: SocketActionTypes.CONNECT_ERROR, error: err});
    };

    const handleDisconnect = (reason: string) => {
      dispatch({type: SocketActionTypes.DISCONNECT, reason});
    };

    const handleOnline = () => {
      // Prompt reconnection when the browser regains network access.
      if (isMounted && activeSocket?.disconnected) activeSocket.connect();
    };

    const connectSocket = async () => {
      try {
        const token = await generateToken();

        if (!isMounted) {
          return;
        }

        const socketInstance = getSocket(buildSocketAuth(token, selfParticipant.name, selfParticipant.avatar));

        activeSocket = socketInstance;
        socketInstance.on(SocketEvents.CONNECT, handleConnect);
        socketInstance.on(SocketEvents.CONNECT_ERROR, handleConnectError);
        socketInstance.on(SocketEvents.DISCONNECT, handleDisconnect);

        setSocket(socketInstance);
        dispatch({
          type: SocketActionTypes.SET_SOCKET,
          payload: socketInstance,
        });

        if (socketInstance.connected) {
          dispatch({type: SocketActionTypes.CONNECT});
        } else if (socketInstance.disconnected) {
          socketInstance.connect();
        }
      } catch (error) {
        if (isMounted) dispatch({type: SocketActionTypes.CONNECT_ERROR, error});
        console.error("Error while generating token or connecting to socket:", error);
      }
    };

    window.addEventListener('online', handleOnline);
    connectSocket();

    return () => {
      isMounted = false;
      window.removeEventListener('online', handleOnline);
      activeSocket?.off(SocketEvents.CONNECT, handleConnect);
      activeSocket?.off(SocketEvents.CONNECT_ERROR, handleConnectError);
      activeSocket?.off(SocketEvents.DISCONNECT, handleDisconnect);
      resetSocket();
      setSocket(null);
    };
  }, [userIdentity?.id, selfParticipant.name, selfParticipant.avatar]);

  useEffect(() => {
    // Check if room list is updated
    if (state.roomListResponse && state.roomListResponse.rooms.length > 0) {
      const roomIds = state?.roomListResponse?.rooms
        ?.map((room) => room?.room_id)
        .filter((roomId) => roomId != null); // Exclude null or undefined IDs

      if (roomIds) {
        CheckOnlineUsers({room_ids: roomIds});
      }
    }
  }, [state.roomListResponse.rooms.length]);
  useEffect(() => {
    // Check if room list is updated
    if (state.systemMessageRooms && state.systemMessageRooms?.rooms?.length > 0) {
      const roomIds = state?.systemMessageRooms?.rooms
        ?.map((room) => room?.room_id)
        .filter((roomId) => roomId != null); // Exclude null or undefined IDs

      if (roomIds) {
        CheckOnlineUsers({room_ids: roomIds});
      }
    }
  }, [state.systemMessageRooms?.rooms?.length]);
  useEffect(() => {
    if (socket && socket.connected) {
      GetUnreadRoomsCount({});
      if (state?.blockListResponse?.success) {
      } else {
        if (
          !(
            state.filter.filter_by === 'all' &&
            state.filter.unread_rooms === false &&
            state.filter.system_messages === false
          )
        ) {
          getBlockList().then(() => getFilteredMessageRooms(state?.filter));
        } else {
          getBlockList().then(() => getRooms({}));
        }
      }
    }

    //state.selectedRooms is in the dependencies needed for syncing correctly
  }, [socket?.id, socket?.connected]);
  useEffect(() => {
    if (socket && socket.connected) {
      socket.on(ChatEvents.TYPING, handleTypingEvent);
      socket.on(ChatEvents.UPDATE_ONLINE_STATUS, updateOnlineUsers);
      socket.on(ChatEvents.UPDATE_MESSAGES, updateMessages);
      socket.on(ChatEvents.BLOCK_USER, handleBlock);
      socket.on(ChatEvents.UNBLOCK_USER, handleUnBlock);

      socket.on(ChatEvents.ARCHIVE_ROOM, handleArchive);
      socket.on(ChatEvents.SEEN, handleSeen);
      socket.on(ChatEvents.DELETE_MESSAGE, handleDelete);

      socket.on(ChatEvents.DELIVERED, handleDelivered);
      socket.on(ChatEvents.SEEN_ALL, handleSeenAll);
    }
    return () => {
      if (socket) {
        socket.off(ChatEvents.TYPING, handleTypingEvent);
        socket.off(ChatEvents.UPDATE_ONLINE_STATUS, updateOnlineUsers);
        socket.off(ChatEvents.UPDATE_MESSAGES, updateMessages);
        socket.off(ChatEvents.BLOCK_USER, handleBlock);
        socket.off(ChatEvents.UNBLOCK_USER, handleUnBlock);

        socket.off(ChatEvents.ARCHIVE_ROOM, handleArchive);
        socket.off(ChatEvents.SEEN, handleSeen);
        socket.off(ChatEvents.DELETE_MESSAGE, handleDelete);

        socket.off(ChatEvents.DELIVERED, handleDelivered);
        socket.off(ChatEvents.SEEN_ALL, handleSeenAll);
      }
    };
    //state.selectedRooms is in the dependencies needed for syncing correctly
  }, [socket?.id, socket?.connected, state.roomListResponse,state.selectedRoom]);
useEffect(() => {
  const pathname = window.location.pathname;

  // ✅ Only run if we're in chats but not /chats/open or /listing
  const isInChats = pathname.includes('/chats');
  const isExcluded =
    pathname.includes('/chats/open') || pathname.includes('/listing');

  if (isInChats && !isExcluded && state.selectedRoomDetails) {
    const isSeller =
      state?.selectedRoomDetails?.seller?.id === userIdentity?.id;

    if (isSeller) {
      setBuyerInfo(state?.selectedRoomDetails?.buyer?.id);
      setRoomListingInfo(state?.selectedRoomDetails?.post?.id);
    } else {
      setRoomListingInfo(state?.selectedRoomDetails?.post?.id);
    }
  }
}, [state.selectedRoom]);

  function handleTypingEvent(data: handleTypingEventInterface) {
    dispatch({
      type: SocketActionTypes.SET_TYPING_STATUS,
      payload: {
        room_id: data.room_id,
        is_typing: data.isTyping,
      },
    });
    if (data.isTyping) {
      if (typingTimeoutRef.current[data.room_id]) {
        clearTimeout(typingTimeoutRef.current[data.room_id] as unknown as number);
      }

      typingTimeoutRef.current[data.room_id] = setTimeout(() => {
        dispatch({
          type: SocketActionTypes.SET_TYPING_STATUS,
          payload: {
            room_id: data.room_id,
            is_typing: false,
          },
        });
      }, 2000);
    }
  }

  const updateOnlineUsers = useCallback((data: UserStatus) => {
    const {user_id, status, socket} = data;
    dispatch({
      type: SocketActionTypes.UPDATE_ONLINE_STATUS,
      response: {user_id: user_id, status: status, socket: null},
    });
  }, []);
  const handleSeenAll = useCallback((data: SeenAllRequest) => {
    dispatch({
      type: SocketActionTypes.SEEN_ALL,
      response: data,
    });
  }, []);
  function handleUnBlock(data: {room_id: string; user_id: string}) {
    if (
      !(
        state.filter.filter_by === 'all' &&
        state.filter.unread_rooms === false &&
        state.filter.system_messages === false
      )
    ) {
      getBlockList().then(() => getFilteredMessageRooms(state?.filter));
    } else {
      getBlockList().then(() => getRooms({}));
    }
  }

  const CheckOnlineUsers = async (
    data: CheckOnlineStatusRequest
  ): Promise<CheckOnlineStatusResponse> => {
    try {
      const response = await emitEvent(socket, ChatEvents.CHECK_ONLINE_STATUS, data);
      dispatch({type: SocketActionTypes.CHECK_ONLINE_STATUS, response});
      return response;
    } catch (error) {
      console.log(error);
      dispatch({
        type: SocketActionTypes.CHECK_ONLINE_STATUS,
        response: [],
      });
      return [{room_id: '', user_id: '', status: false}];
    }
  };

  const unblockUser = async (data: UnblockRequest): Promise<UnBlockResponse | ResponseError> => {
    try {
      const response = await emitEvent(socket, ChatEvents.UNBLOCK_USER, data);
      dispatch({type: SocketActionTypes.UNBLOCK_USER, response});
      return response;
    } catch (error) {
      dispatch({
        type: SocketActionTypes.UNBLOCK_USER,
        response: {
          message: '',
          success: false,
        },
      });
      return {
        message: '',
        success: false,
      };
    }
  };

  const getBlockList = async (): Promise<BlockListResponse | ResponseError> => {
    try {
      const response = await emitEvent(socket, ChatEvents.BLOCK_LIST, {});
      dispatch({type: SocketActionTypes.GET_BLOCK_LIST, response});
      return response;
    } catch (error) {
      dispatch({
        type: SocketActionTypes.GET_BLOCK_LIST,
        response: {
          message: 'error',
          success: false,
          data: {
            items: [],
          },
        },
      });
      return {
        message: 'error',
        success: false,
        data: {
          items: [],
        },
      };
    }
  };
  const GetUnreadRoomsCount = async (p0?: {}): Promise<UnreadRoomsResponse | ResponseError> => {
    try {
      const response = await emitEvent(socket, ChatEvents.rooms_unread_count, {});
      dispatch({type: SocketActionTypes.ROOMS_UNREAD_COUNT, response});
      return response;
    } catch (error) {
      return {
        data: {
          success: false,
          rooms_unread_count: 0,
        },
      };
    }
  };

  const GetUnreadPostRoomCount = async (
    post_id: UnreadPostRoomsRequest
  ): Promise<UnreadPostRoomsResponse> => {
    try {
      const response = await emitEvent(socket, ChatEvents.post_unread_rooms, post_id);
      dispatch({type: SocketActionTypes.POST_UNREAD_ROOMS, response});
      return response;
    } catch (error) {
      return {
        success: false,
        count_rooms: 0,
        post_id: 0,
      };
    }
  };
  const emitDelivered = async (
    data: DeliveredRequest
  ): Promise<DeliveredResponse | ResponseError> => {
    try {
      const response = await emitEvent(socket, ChatEvents.DELIVERED, data);
      dispatch({type: SocketActionTypes.EMIT_DELIVERED, response});
      return response;
    } catch (error) {
      dispatch({
        type: SocketActionTypes.EMIT_DELIVERED,
        response: {message: 'error', success: false},
      });
      return {message: 'error', success: false};
    }
  };

  const emitSeen = useCallback(
    async (data: SeenRequest): Promise<SeenResponse | ResponseError> => {
      try {
        const response = await emitEvent(socket, ChatEvents.SEEN, data);
        const extraResponse = {
          ...response,
          message_id: data.message_id,
          room_id: data.room_id,
        };
        dispatch({type: SocketActionTypes.EMIT_SEEN, response: extraResponse});

        return {
          ...response,
          message_id: data.message_id,
          room_id: data.room_id,
        };
      } catch (error) {
        dispatch({
          type: SocketActionTypes.EMIT_SEEN,
          response: {message: 'error', success: false, message_id: 0, room_id: 'error'},
        });
        return {message: 'error', success: false, message_id: 0, room_id: 'error'};
      }
    },
    [socket]
  );

  const setRoomListingInfo = async (listingId: number): Promise<void> => {
    try {
      if (listingId === 0) {
        dispatch({
          type: SocketActionTypes.SET_LISTING,
          response: {data: 0},
        });
        return;
      }
      const roomCountry = state?.selectedRoomDetails?.post?.country;
      getListingInfoV2List(listingId, roomCountry ? { country: roomCountry } : undefined).then((res) => {
        dispatch({
          type: SocketActionTypes.SET_LISTING,
          response: res.data,
        });
        if(userIdentity?.id!==res?.data?.seller?.id){
           dispatch({
          type: SocketActionTypes.SET_VERIFICATION_LEVEL,
          response: res?.data?.seller?.verification_level,
        });
        }
      });
    } catch (error) {
      console.log('Failed to fetch listing data:', error);
      dispatch({
        type: SocketActionTypes.SET_LISTING,
        response: {
          data: {},
        },
      });
    }finally{
    }
  };
  const setBuyerInfo = async (memberId:number):Promise<void> =>{
      try {
      if (memberId === 0) {
        dispatch({
          type: SocketActionTypes.SET_MEMBER,
          response: {data: 0},
        });
        return;
      }
      getMemberInfoApi(memberId).then((res) => {
           dispatch({
          type: SocketActionTypes.SET_VERIFICATION_LEVEL,
          response: res?.data?.member?.verification_level,
        });
        dispatch({
          type: SocketActionTypes.SET_MEMBER,
          response: res.data,
        });
      });
    } catch (error) {
      console.log('Failed to fetch listing data:', error);
      dispatch({
        type: SocketActionTypes.SET_MEMBER,
        response: {
          data: {},
        },
      });
    }
  };



  const selectRoom = useCallback(
    (response: string) => {
      if (response) {
        params.set('selectedRoom', response);

        navigate(`?${params.toString()}`);
      } else {
        navigate(location.pathname.startsWith('/chats') ? '/chats' : '/');
      }
    },
    [location.pathname, navigate, params]
  );

  const setSelectedRoomDetails = useCallback(
    async (response: string) => {
      try {
        const selectedRoomDetails =
          state?.roomListResponse?.rooms?.find((room) => room?.room_id === response) || null; // Ensure it’s either Room or null

        let room: Room | null = null; // Explicitly define the variable as null initially
        if (!selectedRoomDetails) {
          room = state.systemMessageRooms.rooms.find((room) => room?.room_id === response) || null;
        }
        const currentUserId = Cookies.get('userInfo')
          ? JSON.parse(Cookies.get('userInfo') as string)?.id
          : '';

        const activeRoom = selectedRoomDetails || room; // Pick the valid room
        if (activeRoom && activeRoom.unread_count && activeRoom.unread_count > 0) {
          const latestMessageFromOtherUser = activeRoom.messages?.find(
            (message) => message.recipient_id == currentUserId
          );

          if (latestMessageFromOtherUser) {
            emitSeen({
              room_id: activeRoom.room_id,
              message_id: latestMessageFromOtherUser.id || 0,
            });
          }
        }
        dispatch({type: SocketActionTypes.SELECT_ROOM, roomId: response});

        dispatch({
          type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS,
          response: selectedRoomDetails || room, // Use whichever is found
        });

        if (!selectedRoomDetails && !room) return; // Stop execution if no room is found
      } catch (error) {
        dispatch({
          type: SocketActionTypes.SELECT_ROOM,
          roomId: response,
        });
        console.error('Error selecting room:', error);
      }
    },
    [emitSeen, state?.roomListResponse?.rooms, state.systemMessageRooms.rooms]
  );

  useEffect(() => {
    if (location.pathname === '/chats') {
      const selectedRoom = params.get('selectedRoom');
      setSelectedRoomDetails(selectedRoom || '');
    }
  }, [location.pathname, params, setSelectedRoomDetails]);


  async function emitTyping(data: TypingRequest): Promise<void> {
    try {
      await emitEvent(socket, ChatEvents.TYPING, data);
    } catch (error) {
      console.debug('Typing update was not acknowledged', error);
    }
  }
  const getChatsRedirectPath = useCallback(() => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('selectedRoom');

    const isSellerOfSelectedRoom =
      Number(state?.selectedRoomDetails?.seller?.id) === Number(userIdentity?.id);
    const selectedPostId = state?.selectedRoomDetails?.post?.id;

    if (isSellerOfSelectedRoom && selectedPostId) {
      nextParams.set('id', String(selectedPostId));
    } else {
      nextParams.delete('id');
    }

    const search = nextParams.toString();
    return search ? `/chats?${search}` : '/chats';
  }, [location.search, state?.selectedRoomDetails, userIdentity?.id]);
  const blockUser = async (data: blockRequest): Promise<BlockResponse | ResponseError> => {
    try {
      const response = await emitEvent(socket, ChatEvents.BLOCK_USER, data);
      dispatch({type: SocketActionTypes.BLOCK_USER, response});
      if (state.selectedRoom === data.room_id) {
        dispatch({type: SocketActionTypes.SELECT_ROOM, roomId: undefined});
        dispatch({type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS, response: null});
        navigate(getChatsRedirectPath(), {replace: true});
      }
      return response;
    } catch (error) {
      dispatch({
        type: SocketActionTypes.BLOCK_USER,
        response: {message: 'error', success: false, data: {room_id: '', user_id: ''}},
      });
      return {message: 'error', success: false, data: {room_id: '', user_id: ''}};
    }
  };
  const archiveRoom = async (data: ArchiveRoomRequest): Promise<ArchiveRoomResponse> => {
    try {
      const response = await emitEvent(socket, ChatEvents.ARCHIVE_ROOM, data);

      dispatch({type: SocketActionTypes.EMIT_ARCHIVE_ROOM, response});
      if (data.rooms_ids.some((room_id) => room_id === state?.selectedRoom)) {
        dispatch({type: SocketActionTypes.SELECT_ROOM, roomId: undefined});
        dispatch({type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS, response: null});
        navigate(getChatsRedirectPath(), {replace: true});
      }
      return response;
    } catch (error) {
      dispatch({
        type: SocketActionTypes.EMIT_ARCHIVE_ROOM,
        response: {message: 'error', success: false},
      });

      return {message: 'error', success: false};
    }
  };
  function handleArchive(data: {rooms_ids: Array<string>}) {
    dispatch({
      type: SocketActionTypes.HANDLE_ARCHIVE,
      payload: data,
    });
    if (data.rooms_ids.some((room_id) => room_id === state?.selectedRoom)) {
      dispatch({type: SocketActionTypes.SELECT_ROOM, roomId: undefined});
      dispatch({type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS, response: null});
      navigate(getChatsRedirectPath(), {replace: true});
    }
  }
  function handleBlock(data: {room_id: string; user_id: string}) {
    const isBlockedCurrentRoom =
      state?.selectedRoom === data.room_id ||
      !!state?.selectedRoom?.includes(data.user_id);

    dispatch({
      type: SocketActionTypes.HANDLE_BLOCK,
      payload: data,
    });
    if (
      !(
        state.filter.filter_by === 'all' &&
        state.filter.unread_rooms === false &&
        state.filter.system_messages === false
      )
    ) {
      getBlockList().then(() => getFilteredMessageRooms(state?.filter));
    } else {
      getBlockList().then(() => getRooms({}));
    }
    if (isBlockedCurrentRoom) {
      dispatch({type: SocketActionTypes.SELECT_ROOM, roomId: undefined});
      dispatch({type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS, response: null});
      navigate(getChatsRedirectPath(), {replace: true});
    }
  }
  async function getRooms(data: GetRoomsRequest): Promise<GetRoomsResponse | ResponseError> {
    const session = sessionRef.current;
    try {
      const response = await emitEvent(socket, ChatEvents.GET_ROOMS, data);
      if (session !== sessionRef.current) return {success: false, message: 'Session changed', rooms: [], latest_room_timestamp: null};
      if (!response.success || !Array.isArray(response.rooms)) throw new Error('Room request failed');

      // Iterate over rooms
      response.rooms.map((room) => {
        // Ensure there are messages in the room
        const messages = room?.messages;
        if (messages && messages.length > 0) {
          // Find the first message that is not from the current user and is not delivered
          for (let message of messages) {
            if (message.sender_id !== userIdentity.id && !message.delivered_at) {
              // Emit 'delivered' event for this message
              emitDelivered({
                room_id: room?.room_id,
                message_id: message.id || 0,
              });
              break; // Stop after emitting for the first found message
            }
          }
        }
      });

      // Map the response to the correct Room structure
      const rooms = response.rooms
        .map(
          (room: Room): Room => ({
            room_id: room.room_id,
            seller: room.seller,
            buyer: room.buyer,
            post: room.post,
            messages: room?.messages,
            timestamp: room.timestamp || String(Date.now()),
            last_snippet: room.last_snippet, // Extract snippet if available
            unread_count: room?.unread_count,
            sync_timestamp: room?.sync_timestamp,
            has_more: room?.has_more,
          })
        )
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp)); // Sort rooms by timestamp

      const latest_room_timestamp = rooms[0]?.timestamp || null;

      const roomListResponse: GetRoomsResponse = {
        rooms,
        latest_room_timestamp,
        success: true,
        message: '',
      };

      // Dispatching to context
      dispatch({
        type: SocketActionTypes.GET_ROOMS,
        response: roomListResponse,
      });

      return roomListResponse; // Return the full response
    } catch (error) {
      const errorResponse = {
        message: 'error',
        success: false,
        rooms: [],
        latest_room_timestamp: null,
      };

      if (session === sessionRef.current) {
        dispatch({type: SocketActionTypes.GET_ROOMS, response: errorResponse});
      }

      return errorResponse;
    }
  }

  async function getSystemMessageRooms(
    data: GetRoomsRequest
  ): Promise<GetRoomsResponse | ResponseError> {
    try {
      const response = await emitEvent(socket, ChatEvents.get_system_messages_rooms, data);

      const rooms = response.rooms
        .map(
          (room: Room): Room => ({
            room_id: room.room_id,
            seller: room.seller,
            buyer: room.buyer,
            post: room.post,
            messages: room?.messages,
            timestamp: room.timestamp || new Date().toISOString(),
            last_snippet: room.last_snippet,
            unread_count: room?.unread_count,
            sync_timestamp: room?.sync_timestamp,
            has_more: room?.has_more,
          })
        )
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

      const latest_room_timestamp = rooms[0]?.timestamp || null;

      const roomListResponse: GetRoomsResponse = {
        rooms,
        latest_room_timestamp,
        success: true,
        message: '',
      };

      dispatch({
        type: SocketActionTypes.GET_SYSTEM_MESSAGES_ROOMS,
        response: roomListResponse,
      });

      return roomListResponse;
    } catch (error) {
      const errorResponse = {
        message: 'error',
        success: false,
        rooms: [],
        latest_room_timestamp: null,
      };

      dispatch({
        type: SocketActionTypes.GET_ROOMS,
        response: errorResponse,
      });

      return errorResponse;
    }
  }

  async function getMessages(
    data: GetMessagesRequest
  ): Promise<GetMessagesResponse | ResponseError> {
    const session = sessionRef.current;
    const request = (messageRequestsRef.current.get(data.room_id) ?? 0) + 1;
    messageRequestsRef.current.set(data.room_id, request);
    const isCurrent = () => session === sessionRef.current &&
      messageRequestsRef.current.get(data.room_id) === request;
    try {
      const response = await emitEvent(socket, ChatEvents.GET_MESSAGES, data);
      if (!isCurrent()) return {success: false, message: 'Request superseded', messages: [], has_more: false};
      if (!response.success || !Array.isArray(response.messages)) throw new Error('Message request failed');

      dispatch({
        type: SocketActionTypes.GET_MESSAGES,
        payload: data,
        response: {
          success: true,
          message: '',
          has_more: response.has_more,
          messages: response.messages,
          sync_timestamp: response.sync_timestamp,
        },
      });

      return {
        success: true,
        message: '',
        has_more: response.has_more,
        messages: response.messages,
        sync_timestamp: response.sync_timestamp,
      };
    } catch (error) {
      // Keep the cached history cursor so the same page can be retried.
      return {
        success: false,
        message: 'error',
        has_more: false,
        messages: [],
        sync_timestamp: 0,
      };
    }
  }
  function updateMessages(data: MessageFromRoom) {
    try {
      // if (!data?.message?.id) {
      //   console.warn('Message data is incomplete; skipping update.')
      //   return
      // }

      if(data?.room?.room_id) {
        dispatch({
          type: SocketActionTypes.SET_TYPING_STATUS,
          payload: {
            room_id: data?.room?.room_id,
            is_typing: false,
          },
        });
      }

      const user = JSON.parse(Cookies.get('userInfo') as string).id;

      if (user == data.message.recipient_id) {
        emitDelivered({room_id: data.room.room_id, message_id: data.message.id || 0});
      }
      if (user == data.message.recipient_id && state?.selectedRoom == data.room.room_id) {
        emitSeen({room_id: data.room.room_id, message_id: data.message.id || 0});
      }

      if (
        user == data.message.recipient_id &&
        window.location.href.includes('chats/open') &&
        state?.selectedRoom === data?.room?.room_id
      ) {
        emitSeen({room_id: data.room.room_id, message_id: data.message.id || 0});
      }

      dispatch({
        type: SocketActionTypes.UPDATE_MESSAGES,
        payload: {...data, currentUser: userIdentity.id},
      });
      if (
        !state?.roomListResponse?.rooms?.some((room) => room?.room_id === data.room.room_id) &&
        !state?.systemMessageRooms?.rooms?.some((room) => room?.room_id === data.room.room_id)
      ) {
        if (
          !(
            state.filter.filter_by === 'all' &&
            state.filter.unread_rooms === false &&
            state.filter.system_messages === false
          )
        ) {
          getFilteredMessageRooms(state?.filter);
        } else {
          getRooms({});
        }
      }
    } catch (error) {
      console.log(error);
    }
  }
  function handleDelivered(data: {message_id: string; room_id: string}) {
    dispatch({
      type: SocketActionTypes.HANDLE_DELIVERED,
      payload: data,
    });
  }
  function handleSeen(data: {message_id: string; room_id: string}) {
    dispatch({
      type: SocketActionTypes.HANDLE_SEEN,
      payload: data,
    });
  }
  function handleDelete(data: {message_id: number; room_id: string; user_id: number}) {
    dispatch({
      type: SocketActionTypes.HANDLE_DELETE,
      payload: data,
    });
  }
  const setFilterStatus = (filter: {
    filter_by: 'all' | 'buyer' | 'seller' | 'listing_id' | 'verified';
    unread_rooms: boolean;
    system_messages: boolean;
  }) => {
    filteredRoomsRequestRef.current++;
    dispatch({
      type: SocketActionTypes.SET_FILTER_STATUS,
      payload: filter, // Store the entire filter object
    });
  };
  function fetchRoom(listingId: number, sellerId: number, buyerId: number) {
    dispatch({
          type: SocketActionTypes.SET_VERIFICATION_LEVEL,
          response: 0,
        });
    try {
      const roomId = `${listingId}-${sellerId}-${buyerId}`;
      const roomCountry2 = state?.selectedRoomDetails?.post?.country;
      const isSellerViewer = Number(userIdentity?.id) === Number(sellerId) && Number(sellerId) !== Number(buyerId);
      const redirectToOpenSooq = () => {
        window.location.href = generateOpenSooqRedirectUrl(
          process.env.REACT_APP_OPENSOOQ_BASE ?? '',
          getCookies().ecountry ?? '',
          lang
        );
      };
      Promise.all([
        getListingInfoV2List(listingId, roomCountry2 ? { country: roomCountry2 } : undefined),
        isSellerViewer ? getMemberInfoApi(buyerId) : Promise.resolve(null),
        isSellerViewer ? getMessages({room_id: roomId}) : Promise.resolve(null),
      ])
        .then(async ([response, buyerResponse, sellerHistory]) => {
          const listing = response?.data?.listing;
          const seller = response?.data?.seller;
          const buyerMember = buyerResponse?.data?.member ?? buyerResponse?.data?.member_basic;
          const buyerChatInfo = isSellerViewer && !buyerMember?.full_name
            ? await emitEvent(socket, ChatEvents.user_info, {user_id: buyerId}).catch(() => null)
            : null;
          const buyerName = buyerMember?.full_name || buyerChatInfo?.name;
          const buyerAvatar = buyerMember?.profile_picture || buyerChatInfo?.avatar;

          if (sellerId !== seller.id) {
            redirectToOpenSooq();
            return;
          }
          if (!isSellerViewer && userIdentity?.id !== buyerId) {
            redirectToOpenSooq();
            return;
          }
          const sellerRoomExists = !!sellerHistory && 'success' in sellerHistory && sellerHistory.success &&
            sellerHistory.messages.length > 0;
          if (isSellerViewer && !sellerRoomExists) {
            redirectToOpenSooq();
            return;
          }
          dispatch({
          type: SocketActionTypes.SET_LISTING,
          response: response.data,
        });
           dispatch({
          type: SocketActionTypes.SET_VERIFICATION_LEVEL,
          response: isSellerViewer ? buyerMember?.verification_level : response?.data?.seller?.verification_level,
        });
          if (isSellerViewer) {
            dispatch({
              type: SocketActionTypes.SET_MEMBER,
              response: buyerResponse?.data,
            });
          }
          selectRoom(roomId);
          setSelectedRoomDetails(roomId);
          const sellerParticipant = mergeParticipant(participantFromListingSeller(seller), null);
          const room: Room = {
            sync_timestamp: 0,
            has_more: false,
            room_id: roomId,
            seller: {
              id: sellerId,
              name: sellerParticipant.name,
              avatar: sellerParticipant.avatar,
              is_shop: sellerParticipant.is_shop,
              verification_level: seller?.verification_level ?? 0,
            },
            buyer: isSellerViewer
              ? {
                  id: Number(buyerId),
                  avatar: buyerAvatar,
                  name: buyerName,
                  verification_level: buyerMember?.verification_level ?? 0,
                }
              : {
                  id: Number(buyerId),
                  avatar: selfParticipant.avatar,
                  name: selfParticipant.name,
                  verification_level: userIdentity?.verification_level ?? 0,
                },
            post: {
              country: listing?.country_abbreviation,
              id: listingId,
              title: listing?.title,
              cv_id: listing?.cv_id,
              uri: listing?.first_image_uri,
              type: listing?.post_type,
            },
            messages: [],
            timestamp: Date.now().toString(),
          };
          dispatch({type: SocketActionTypes.SET_SELECTED_ROOM_DETAILS, response: room});
          if (!isSellerViewer) getMessages({room_id: roomId});
        })
        .catch((error) => {
          console.log(error);
          redirectToOpenSooq();
        });
    } catch (error) {
      console.log(error);
    }finally{
    }
  }
  async function getUserInfo(user_id: userInfoRequest): Promise<UserInfoResponse> {
    try {
      const response = await emitEvent(socket, ChatEvents.user_info, user_id);
      dispatch({type: SocketActionTypes.USER_INFO, response});
      return response;
    } catch (error) {
      return {
        uid: 0,
        response_time: 0,
        name: '',
        avatar: '',
        resp_time_ver: 0,
        real_response_time: 0,
        answered_response_time: 0,
        res_rate: 0,
        avg_response_time: 0,
      };
    }
  }
  async function sendMessage(data: SendMessageRequest): Promise<SendMessageResponse> {
    const room_id = data.room.room_id || '';
    const message_id = data.message.id ?? generateRandomNumber();
    const session = sessionRef.current;
    const isCurrentSession = () =>
      session === sessionRef.current && identityRef.current?.id === data.message.sender_id;
    const failure: SendMessageResponse = {
      success: false, message: 'send_failed',
      data: {room: {...data.room, room_id}, message: data.message},
    };
    const key = `${data.message.sender_id}:${room_id}:${message_id}`;
    if (!room_id || !isCurrentSession() || sendingRef.current.has(key)) return failure;
    sendingRef.current.add(key);
    // The local ID tracks this attempt in the UI; it is not a server idempotency key.
    const queued = {
      ...data,
      message: {
        ...data.message,
        id: message_id,
        sent_at: data.message.sent_at || Date.now().toString(),
        pending: true,
        failed: false,
      },
    };
    addPendingMessageToQueue(queued, room_id);
    try {
      const blockedRecipient = state?.blockListResponse?.data?.items?.some(
        (item) => item?.user_id === data?.message?.recipient_id
      );

      if (blockedRecipient) {
        failure.message = 'blocked_user';
        throw new Error('Recipient is blocked');
      }

      // Prefer the current profile for the sender; reuse real room names for the other party.
      const room = {...data.room, seller: {...data.room.seller}, buyer: {...data.room.buyer}};
      for (const role of ['seller', 'buyer'] as const) {
        const participant = room[role];
        const selected = state.selectedRoomDetails?.[role];
        const name = (participant.id === userIdentity?.id ? userIdentity.full_name?.trim() : '') ||
          participant.name?.trim() || (selected?.id === participant.id ? selected.name?.trim() : '');
        if (!name) throw new Error(`Missing required ${role} name`);
        room[role].name = name;
      }
      const {id, pending, failed, ...message} = data.message;
      const response = await emitEvent(socket, ChatEvents.SEND_MESSAGE, {...data, room, message});
      if (!isCurrentSession()) return failure;
      if (typeof response.success !== 'boolean') throw new Error('Invalid send acknowledgement');
      if (response.success === true) {
        removeQueuedMessage(room_id, message_id);
        if (response.data) updateMessages(response.data);
        return response;
      }
      addFailedMessageToQueue({...queued, message: {...queued.message, pending: false, failed: true}}, room_id);
      return response;
    } catch (error) {
      console.log(error);
      if (isCurrentSession()) {
        addFailedMessageToQueue({...queued, message: {...queued.message, pending: false, failed: true}}, room_id);
      }
      return failure;
    } finally {
      sendingRef.current.delete(key);
    }
  }
  async function deleteMessage(data: DeleteMessageRequest): Promise<DeleteMessageResponse | Error> {
    const {room_id} = data;

    try {
      // Attempt to emit the event and get a response
      const response = await emitEvent(socket, ChatEvents.DELETE_MESSAGE, data);
      dispatch({type: SocketActionTypes.DELETE_MESSAGE, response});

      // Archive the room if it's the last message
      if (state.getMessagesResponse[room_id]?.messages.length === 1) {
        archiveRoom({rooms_ids: [room_id]});
      }

      return response;
    } catch (error) {
      console.log('Error in deleteMessage:', error);

      // Ensure a consistent structure for the error response
      const response: DeleteMessageResponse = {
        message: 'An error occurred while deleting the message',
        success: false,
        data: {room_id: data.room_id, message_id: data.message_id}, // Fallback or placeholder values
      };

      // Dispatch the error response for state handling
      dispatch({type: SocketActionTypes.DELETE_MESSAGE, response});

      return response;
    }
  }
  async function getFilteredMessageRooms(
    data: filterRequest
  ): Promise<GetRoomsResponse | ResponseError> {
    const session = sessionRef.current;
    const request = ++filteredRoomsRequestRef.current;
    try {
      const response = await emitEvent(socket, ChatEvents.get_rooms_with_options, data);
      if (session !== sessionRef.current || request !== filteredRoomsRequestRef.current) {
        return {success: false, message: 'Request superseded', rooms: [], latest_room_timestamp: null};
      }
      if (!response.success || !Array.isArray(response.rooms)) throw new Error('Filtered room request failed');
      const blockedUsers = new Set(
        state?.blockListResponse?.data?.items?.map((user) => user?.user_id)
      );
      const rooms = response?.rooms
        .map(
          (room: Room): Room => ({
            room_id: room.room_id,
            seller: room.seller,
            buyer: room.buyer,
            post: room.post,
            messages: room?.messages,
            timestamp: room.timestamp || new Date().toISOString(),
            last_snippet: room.last_snippet,
            unread_count: room?.unread_count,
            sync_timestamp: room?.sync_timestamp,
            has_more: room?.has_more,
          })
        )
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
        .filter((room) => {
          const containsBlockedUser = Array.from(blockedUsers).some((userId) =>
            roomHasUser(room, userId)
          );

          return !containsBlockedUser;
        });

      const latest_room_timestamp = rooms[0]?.timestamp || null;
      // Filter incoming rooms to only include new ones
      const roomListResponse: GetRoomsResponse = {
        rooms,
        latest_room_timestamp,
        success: true,
        message: '',
      };

      dispatch({
        type: SocketActionTypes.GET_ROOMS_WITH_OPTIONS,
        response: roomListResponse,
      });

      return roomListResponse;
    } catch (error) {
      const errorResponse = {
        message: 'error',
        success: false,
        rooms: [],
        latest_room_timestamp: null,
      };

      return errorResponse;
    }
  }
  useEffect(() => {
    if (state.socket?.connected) {
      resendFailedMessages();
    }
  }, [state.connectionStatus, state.socket]);
  const resendFailedMessages = async () => {
    const session = sessionRef.current;
    if (state.failedMessages && state.socket?.connected) {
      for (const room_id in state.failedMessages) {
        const failedMessagesQueue = state.failedMessages[room_id];

        if (!failedMessagesQueue || failedMessagesQueue.length === 0) continue;

        for (const failed_Message of failedMessagesQueue) {
          if (!state.socket.connected || session !== sessionRef.current) return;
          // Another retry or a user removal may have consumed this snapshot item.
          const currentMessage = failedMessagesRef.current?.[room_id]?.find(
            item => item.message.id === failed_Message.message.id
          );
          if (!currentMessage) continue;
          const response = await sendMessage({
            room: {
              ...currentMessage.room,
              post: {
                ...currentMessage.room?.post,
                cv_id: currentMessage.room?.post?.cv_id ?? '',
              },
            },
            message: {...currentMessage.message, failed: false},
          });
          if (response.success) {
            const isSeller = userIdentity?.id == failed_Message?.room.seller.id;

            logMessageClick(
              {
                country: failed_Message?.room?.post?.country,
                id: failed_Message?.room?.post?.id,
                memberId: failed_Message.room.seller?.id,
              },
              isSeller,
              100
            );
          }
        }
      }
    }
  };
  const connectSocket = () => {
    try {
      if (socket && !socket.connected) {
        socket.connect();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const disconnectSocket = () => {
    try {
      if (socket && socket.connected) {
        socket.disconnect();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const addFailedMessageToQueue = (messageData: SendMessageRequest, room_id: string) => {
    dispatch({
      type: SocketActionTypes.ADD_FAILED_MESSAGES,
      response: {messageData, room_id},
    });
  };
  const removeQueuedMessage = (room_id: string, message_id: number) => {
    // Make acceptance/removal visible to an active retry loop before React rerenders.
    failedMessagesRef.current = {...failedMessagesRef.current, [room_id]:
      failedMessagesRef.current?.[room_id]?.filter(item => item.message.id !== message_id) || []};
    dispatch({type: SocketActionTypes.REMOVE_QUEUED_MESSAGE, response: {room_id, message_id}});
  };
  const updateFailedMessageQueue = (messages: SendMessageRequest[], room_id: string) => {
    dispatch({
      type: SocketActionTypes.UPDATE_FAILED_MESSAGES,
      response: {messages, room_id},
    });
  };

  const addPendingMessageToQueue = (messageData: SendMessageRequest, room_id: string) => {
    dispatch({
      type: SocketActionTypes.ADD_PENDING_MESSAGES,
      response: {messageData, room_id},
    });
  };

  const updatePendingMessageQueue = (messages: SendMessageRequest[], room_id: string) => {
    dispatch({
      type: SocketActionTypes.UPDATE_PENDING_MESSAGES,
      response: {messages, room_id},
    });
  };

  useEffect(() => {
    if (
      !(
        state.filter.filter_by === 'all' &&
        state.filter.unread_rooms === false &&
        state.filter.system_messages === false
      )
    ) {
      getBlockList().then(() => getFilteredMessageRooms(state?.filter));
    } else {
      getBlockList().then(() => getRooms({}));
    }
  }, [state.blockResponse, state.unblockResponse, state.archiveRoomResponse]);
  return (
    <SocketContext.Provider
      value={{
        socket,
        connectionStatus: state.connectionStatus,
        connectSocket,
        disconnectSocket,
        blockUser,
        unblockUser,
        getBlockList,
        emitDelivered,
        emitSeen,
        emitTyping,
        emitArchiveRoom: archiveRoom,
        getRooms,
        getMessages,
        sendMessage,
        removeQueuedMessage,
        deleteMessage,
        selectRoom,
        state,
        dispatch,
        addFailedMessageToQueue,
        updateFailedMessageQueue,
        addPendingMessageToQueue,
        updatePendingMessageQueue,
        fetchRoom,
        getSystemMessageRooms,
        GetUnreadRoomsCount,
        GetUnreadPostRoomCount,
        getFilteredMessageRooms,
        getUserInfo,
        setRoomListingInfo,
        setFilterStatus,
        updateUnreadRoomsCount,
        setBuyerInfo,
        selfParticipant,

        setSelectedRoomDetails,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export {SocketContext};
